/**
 * Sync Service
 *
 * Handles periodic synchronization with the Content API.
 * Fetches media library updates and notifies stores.
 * Supports SSE for real-time notifications with polling fallback.
 */

import axios, { AxiosInstance } from 'axios';
import { SyncRequest, SyncResponse } from '~/api/types/sync';
import { transformSyncResponse } from '~/api/transforms/mediaSync';
import { weatherStore } from '~/stores/weather.store';
import { detectEngineVersion } from '~/utils/engineVersion';
import { getUptime } from '~/utils/uptime';
import analyticsDB from '~/services/analytics/AnalyticsDB';
import { coletarCacheDeMidia } from '~/services/health/mediaCacheStats';
import { coletarMemoriaDoSistema } from '~/services/health/systemMemory';
import { lerSseZumbis } from '~/services/health/sseTelemetry';
import {
  createContentService,
  resolveDiscoveryMode,
} from '~/services/sse/createContentService';
import {
  SSEConnectionState,
  SSEEvent,
  IContentDiscoveryService,
} from '~/services/sse/types';
import { unifiedContentChannel } from '~/services/sse/unifiedContentChannel';
import { computeContentResyncDelayMs } from './contentResyncJitter';
import { kioskStore } from '~/stores/kiosk.store';
import { getNativeKioskBackend } from '~/services/platform/kioskMode';
import {
  hasHostVolumeState,
  isHostVolumeControlled,
  getHostVolumeLevel,
} from '~/services/embedded/hostVolumeControl';
import { deviceTokenService } from '~/services/auth/DeviceToken';
import { recordSyncSample, applyServerTime } from '~/services/time/serverClock';
import {
  logAuthAwareError,
  isWaitingDeviceApproval,
  triggerAuthRecovery,
} from '~/utils/deviceApprovalState';
import { LAST_RELOAD_AT_KEY } from '~/services/storageKeys';
import { lerDecodersDeVideoEmUso } from '~/services/playback/useSegundoDecoder';
import { hostAarVersion } from '~/services/embedded/hostConfig';
import {
  ISyncService,
  SyncResult,
  SyncCallback,
  ConnectivityCallback,
  SyncServiceConfig,
  ConfigUpdateCallback,
  TriggerFiredCallback,
  TriggerDeactivatedCallback,
  RestartCommandCallback,
  SyncCorrectionPayload,
  SyncCorrectionCallback,
  RestartCommandPayload,
} from './types';

const DEFAULT_POLLING_INTERVAL = 30 * 1000; // 30 seconds (reduced from 5 minutes)
// 2026-05-24 — reduzido de 5min → 60s. Race condition observada em prod:
// TV Tizen com SSE healthy (heartbeats chegando) perdeu `trigger_fired`
// individual entre server e client (TCP packet drop ou buffer corrompido).
// EventSource não tem ACK aplicação — perdeu, perdeu. Polling de 5min é
// muito comparado a trigger ONCE (10s execução). 60s garante que TV pega
// trigger no MAX em ~1min mesmo se SSE perder evento específico.
// Custo: 5000 TVs × 1 req/min = 83 req/s extra no /api/v1/sync/media-library.
const DEFAULT_SSE_CONNECTED_POLLING_INTERVAL = 60 * 1000; // 60 seconds when SSE connected
const DEFAULT_RETRY_DELAY = 30 * 1000; // 30 seconds
const DEFAULT_MAX_RETRIES = 3;
const SYNC_ENDPOINT = '/api/v1/sync/media-library';

/**
 * Janela mínima entre snapshots de saúde enviados no sync (Fase 2).
 *
 * O sync roda a cada 60 s MESMO com SSE conectado (vide
 * DEFAULT_SSE_CONNECTED_POLLING_INTERVAL), então sem esta janela seriam ~1.440
 * linhas/dia/player no histórico. Com ela são 96 — e 96 é o número real, não um
 * teto: sempre há sync disponível quando a janela abre.
 */
const HEALTH_SAMPLE_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Payload cru de zona vindo do server — aceita snake_case E camelCase (o server
 * historicamente enviou os dois). Só usado no parse defensivo de `data.zones`,
 * que é normalizado pra `SyncZone` (camelCase) logo em seguida.
 */
type RawZonePayload = {
  id?: unknown;
  name?: unknown;
  pos_x?: unknown;
  posX?: unknown;
  pos_y?: unknown;
  posY?: unknown;
  width?: unknown;
  height?: unknown;
  z_order?: unknown;
  zOrder?: unknown;
  layout_width?: unknown;
  layoutWidth?: unknown;
  layout_height?: unknown;
  layoutHeight?: unknown;
};

class SyncService implements ISyncService {
  private api: AxiosInstance;
  private config: Required<SyncServiceConfig>;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  /** Último snapshot de saúde enviado. Vide HEALTH_SAMPLE_INTERVAL_MS. */
  private ultimoSnapshotSaude = 0;
  /** Itens que o último sync com conteúdo mandou baixar (vide `snapshotDeSaude`). */
  private ultimoTotalDeMidia: number | null = null;
  private lastSyncTime: string | null = null;
  private libraryHash: string | null = null;
  private retryCount = 0;

  private syncCallbacks: Set<SyncCallback> = new Set();

  /**
   * Mecanismo em uso. Começa pelo ambiente e passa a ser ditado pelo servidor
   * na primeira resposta de sync que trouxer `discovery_mode`.
   */
  private discoveryMode: 'sse' | 'polling';

  /** Sync agendado com o jitter do servidor — ver scheduleSyncAfterChange. */
  private pendingSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private connectivityCallbacks: Set<ConnectivityCallback> = new Set();
  private configCallbacks: Set<ConfigUpdateCallback> = new Set();
  private triggerFiredCallbacks: Set<TriggerFiredCallback> = new Set();
  private triggerDeactivatedCallbacks: Set<TriggerDeactivatedCallback> =
    new Set();
  private restartCommandCallbacks: Set<RestartCommandCallback> = new Set();
  private syncCorrectionCallbacks: Set<SyncCorrectionCallback> = new Set();
  private orientationFromAdminCallbacks: Set<
    (orientation: 'landscape' | 'portrait') => void
  > = new Set();

  // SSE Integration
  private sseService: IContentDiscoveryService | null = null;
  private sseConnected = false;
  private currentPollingIntervalMs: number;

  /**
   * Cancelamento da inscrição no {@code unifiedContentChannel} (Epic D, Fase D2).
   * Não-nulo SOMENTE quando o conteúdo chega pelo canal SSE unificado (modo SSE +
   * flag ON) — nesse caso {@code sseService} fica `null` (sem 2ª conexão).
   */
  private unifiedContentUnsub: (() => void) | null = null;

  /**
   * Inscrição DEDICADA pra batida de sincronismo (`sync_beat`), independente do modo
   * de descoberta. O RemoteOps publica a batida no canal unificado sempre; mas o
   * SyncService só assinava o canal no modo SSE — em modo polling (o default de boot)
   * ninguém ouvia e o relógio do videowall só se corrigia a cada 30 min, pelo sync.
   */
  private syncBeatUnsub: (() => void) | null = null;
  private lastBeatKey: string | null = null;

  /**
   * Backend de kiosk ativo (cached do plugin nativo).
   * KIOSK_HARDENING_PLAN: enviado em cada SyncRequest pra server saber
   * qual modo cada tablet está usando (telemetria do admin).
   * Lido uma vez no primeiro sync e cacheado — evita custo a cada poll.
   */
  private kioskBackendCached: string | null = null;

  /**
   * DeviceInfo cacheado do platform service. Coletado 1x no primeiro sync
   * (getDeviceInfo é async e envolve webapis nativas em Tizen — DUID,
   * firmware, realModel) e reusado nos syncs subsequentes. Enviado no
   * request pra server hidratar `tab_players.metadata` — fecha o gap dos
   * players auto-registrados pelo DeviceApiKeyFilter (2026-07-27 Renato).
   *
   * Não bloqueia o sync: se coleta falhar/demorar, primeiro sync vai sem
   * device_info; próximo tenta de novo. Best-effort.
   *
   * 🔴 SÓ CAMPO ESTÁTICO AQUI. Isto é reenviado IDÊNTICO em todo sync, então
   * qualquer métrica que mude vira um valor congelado no boot. Foi o que
   * aconteceu com o snapshot de saúde (2026-09-23): a série histórica virou a
   * mesma linha repetida. Métrica viva entra em {@link #montarDeviceInfoDoSync}.
   */
  private deviceInfoCached: SyncRequest['device_info'] | null = null;

  /**
   * Tags do HOST embedded (Fase 4a/§16). `null` = host NUNCA setou → NÃO enviamos
   * `tags` no sync (frota não-embedded fica intacta: server no-op). Uma vez setado
   * (mesmo `[]`), enviamos em TODO sync — o server tem dirty-check próprio
   * (`metadata.hostTagsSig`), então repetir é barato e auto-cura reconciliações
   * perdidas. Persistido em localStorage → sobrevive a `location.reload()` (o host
   * manda `start` no boot do WebView, mas um reload interno não re-dispararia).
   */
  private hostTags: string[] | null = null;
  private static readonly HOST_TAGS_STORAGE_KEY = 'efflux_host_tags';
  private static readonly HOST_TAGS_MAX = 30;
  private static readonly HOST_TAG_MAX_LEN = 64;

  constructor(config: SyncServiceConfig) {
    this.hostTags = SyncService.loadPersistedHostTags();
    this.discoveryMode = resolveDiscoveryMode(config.initialDiscoveryMode);
    this.config = {
      ...config,
      pollingIntervalMs: config.pollingIntervalMs ?? DEFAULT_POLLING_INTERVAL,
      retryDelayMs: config.retryDelayMs ?? DEFAULT_RETRY_DELAY,
      maxRetries: config.maxRetries ?? DEFAULT_MAX_RETRIES,
      apiKey: config.apiKey ?? '', // Deprecated, kept for backward compatibility
      // Do ambiente quando não informado: a tela conhece o próprio tenant, e o
      // servidor usa isso só como chave de cache — não como credencial.
      tenantId: config.tenantId ?? import.meta.env.VITE_TENANT_ID ?? '',
      sseApiUrl: config.sseApiUrl ?? '',
      sseEnabled: config.sseEnabled ?? true,
      sseConnectedPollingIntervalMs:
        config.sseConnectedPollingIntervalMs ??
        DEFAULT_SSE_CONNECTED_POLLING_INTERVAL,
      // Já resolvido acima; guardado aqui só para o config ficar completo.
      // Depois do primeiro sync quem manda é o servidor, e o valor daqui não é
      // mais consultado.
      initialDiscoveryMode: this.discoveryMode,
    };

    this.currentPollingIntervalMs = this.config.pollingIntervalMs;

    this.syncBeatUnsub = unifiedContentChannel.onContentEvent(
      this.handleSyncBeat,
    );

    // Build headers with device ID for authentication
    // Server validates by device ID (checks if device is approved)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.config.deviceId) {
      headers['X-Device-Id'] = this.config.deviceId;
      console.log(
        '[SyncService] Device ID configured for authentication:',
        this.config.deviceId,
      );
    } else {
      console.warn(
        '[SyncService] No Device ID configured - sync requests will fail',
      );
    }

    // Add X-Device-Token (gerado/persistido em DeviceTokenService) em TODO
    // request do sync. O backend usa isso pra manter tab_players.device_token
    // sincronizado com o token atual do tablet, mesmo após reinstall do APK
    // (que pode limpar o localStorage do WebView, regenerando o token).
    // Sem isso, o SSE de Remote Ops (Visualização Interativa) rejeita o
    // player com 401 porque o DB tem um token "antigo" preso, e ele fica
    // eternamente "device offline" no admin. O sync confirma identidade via
    // X-Device-Id (hardware ID) — o mesmo nível de trust permite atualizar
    // o token. Vide DeviceApiKeyFilter#syncDeviceTokenIfChanged.
    //
    // ⚠️ Usamos getToken() (NÃO getTokenOrNull()) porque o SyncService é
    // construído ANTES do useRemoteOpsInit. Se usasse getTokenOrNull e o
    // token ainda não tivesse sido gerado, retornaria null → header não
    // viajaria nunca, e o sync regular jamais conseguiria sincronizar o
    // device_token no DB. getToken() gera+persiste se não existir.
    try {
      const deviceToken = deviceTokenService.getToken();
      if (deviceToken) {
        headers['X-Device-Token'] = deviceToken;
      }
    } catch {
      // localStorage indisponível em alguns ambientes (SSR/test) — ignora
    }

    this.api = axios.create({
      baseURL: this.config.contentApiUrl,
      timeout: 30000,
      headers,
    });

    // 2026-05-24 — Diagnostic instrumentation pra investigar bug "request não
    // chega no servidor" no Android tablet. WebView do Capacitor consegue
    // ping/curl mas o XHR do app não aparece nos logs do servidor. Hipóteses:
    // CORS preflight bloqueando (androidScheme: 'http' faz origin ser
    // http://localhost, requisições pra https://api.efflux.media disparam
    // preflight CORS), cert SSL específico do WebView, header obrigatório
    // faltando, etc. Esses logs deixam visível no chrome://inspect remoto
    // exatamente o que está acontecendo em cada request/response/error.
    //
    // ⚠️ Apenas LOGGING — não muda comportamento. Após identificar root
    // cause via console remoto, fix de verdade vai em commit separado.
    this.api.interceptors.request.use(
      (config) => {
        const fullUrl = `${config.baseURL ?? ''}${config.url ?? ''}`;
        // HTTP logs: alto volume — debug only
        console.debug('[SyncService][HTTP→] Request outbound', {
          method: (config.method ?? 'get').toUpperCase(),
          fullUrl,
          baseURL: config.baseURL,
          url: config.url,
          // Headers podem conter info sensível (X-Device-Token). Filtramos
          // pra logar só nomes + valores não-sensíveis. Token vira length-only.
          headers: this.redactHeadersForLog(config.headers),
          hasData: config.data !== undefined,
          dataSize:
            typeof config.data === 'string'
              ? config.data.length
              : config.data
                ? JSON.stringify(config.data).length
                : 0,
          timeoutMs: config.timeout,
          // navigator.onLine pode mentir em Android WebView mas é um data point
          navigatorOnline:
            typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
          // Origin do WebView — útil pra diagnosticar CORS
          origin:
            typeof window !== 'undefined' && window.location
              ? window.location.origin
              : 'unknown',
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        });
        return config;
      },
      (error) => {
        console.error('[SyncService][HTTP→] Request setup failed:', error);
        return Promise.reject(error);
      },
    );

    this.api.interceptors.response.use(
      (response) => {
        console.debug('[SyncService][HTTP←] Response received', {
          status: response.status,
          statusText: response.statusText,
          fullUrl: `${response.config?.baseURL ?? ''}${response.config?.url ?? ''}`,
          // Server-side log marker — se aparecer no servidor mas não chegar
          // aqui, é problema de network entre WebView e fora.
          responseHeaders: this.summarizeResponseHeaders(response.headers),
          dataSize:
            typeof response.data === 'string'
              ? response.data.length
              : response.data
                ? JSON.stringify(response.data).length
                : 0,
        });
        return response;
      },
      (error) => {
        // Esse é o ponto crítico. Em axios, falhas de rede / CORS / preflight
        // / timeout / DNS / SSL pinning aparecem TODAS como error genérico.
        // Logamos todos os campos discriminantes pra distinguir entre causas
        // — sem isso, "Network Error" no chrome inspect é indistinguível
        // de CORS vs DNS vs timeout etc.
        const cfg = error.config;
        const isAxiosErr = axios.isAxiosError(error);
        const fullUrl = cfg
          ? `${cfg.baseURL ?? ''}${cfg.url ?? ''}`
          : 'unknown';

        console.error('[SyncService][HTTP←] Request failed', {
          fullUrl,
          // error.code: ERR_NETWORK | ECONNABORTED (timeout) | ERR_BAD_REQUEST |
          // ERR_BAD_RESPONSE | undefined (em CORS preflight fail, fica
          // undefined porque o XHR nem completa)
          errorCode: error.code,
          errorName: error.name,
          errorMessage: error.message,
          isAxiosError: isAxiosErr,
          // Se hasResponse=true, server respondeu (mesmo que com 4xx/5xx).
          // Se hasRequest=true mas hasResponse=false, request saiu mas não
          // recebeu resposta (timeout, network drop, CORS preflight block,
          // SSL fail). hasRequest=false significa que nem chegou a sair.
          hasResponse: !!error.response,
          hasRequest: !!error.request,
          responseStatus: error.response?.status,
          responseStatusText: error.response?.statusText,
          responseHeaders: error.response
            ? this.summarizeResponseHeaders(error.response.headers)
            : undefined,
          responseData: error.response?.data
            ? typeof error.response.data === 'string'
              ? error.response.data.slice(0, 500)
              : JSON.stringify(error.response.data).slice(0, 500)
            : undefined,
          // XHR-level info (quando disponível) — chrome inspect mostra mais
          // detalhes na aba Network mas isso aqui vai pro console pra
          // remote logger
          requestReadyState: error.request?.readyState,
          requestStatus: error.request?.status,
          requestStatusText: error.request?.statusText,
          requestResponseURL: error.request?.responseURL,
          // Pista de CORS — se request.responseURL é diferente do url
          // configurado, houve redirect
          configMethod: cfg?.method?.toUpperCase(),
          configHeaders: cfg
            ? this.redactHeadersForLog(cfg.headers)
            : undefined,
          configTimeoutMs: cfg?.timeout,
          // Conectividade local na hora da falha
          navigatorOnline:
            typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
        });
        return Promise.reject(error);
      },
    );

    // Initialize SSE if enabled and URL provided
    // BUT only if we have a valid (non-temp) device ID
    if (
      this.config.sseEnabled &&
      this.config.sseApiUrl &&
      !this.isTemporaryId(this.config.deviceId)
    ) {
      this.initSSE();
    } else if (
      this.config.sseEnabled &&
      this.isTemporaryId(this.config.deviceId)
    ) {
      console.log(
        '[SyncService] SSE init deferred - waiting for valid Device ID',
      );
    }

    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }

    // 2026-05-24 — Snapshot do ambiente HTTP no boot. Útil pra confirmar
    // VITE_CONTENT_API_URL no APK distribuído == valor esperado (https://
    // api.efflux.media), e pra ver origin do WebView (vai indicar se androidScheme
    // é http://localhost ou https://localhost).
    console.log('[SyncService][HTTP] Boot snapshot', {
      contentApiUrl: this.config.contentApiUrl,
      sseApiUrl: this.config.sseApiUrl,
      sseEnabled: this.config.sseEnabled,
      deviceId: this.config.deviceId,
      clientVersion: this.config.clientVersion,
      pollingIntervalMs: this.config.pollingIntervalMs,
      origin:
        typeof window !== 'undefined' && window.location
          ? window.location.origin
          : 'unknown',
      protocol:
        typeof window !== 'undefined' && window.location
          ? window.location.protocol
          : 'unknown',
      navigatorOnline:
        typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    });
  }

  /**
   * Coleta info do device via `window.__effluxPlatform` (singleton exposto
   * pelo `PlatformContext`). Retorna `null` se o platform service ainda
   * não estiver disponível (boot muito precoce) ou se `getDeviceInfo`
   * falhar. Sem circular dep — usa o mesmo padrão de acesso que
   * `sync.store.ts:665`.
   *
   * Cacheado em `deviceInfoCached` após 1ª chamada bem-sucedida — sync
   * subsequente reusa (getDeviceInfo em Tizen envolve várias webapis
   * nativas: `productinfo.getDuid/getRealModel/getFirmware`, `network.getIp`).
   */
  private async resolveDeviceInfo(): Promise<
    SyncRequest['device_info'] | null
  > {
    const platformService = (
      window as unknown as {
        __effluxPlatform?: {
          getDeviceInfo?: () => Promise<Record<string, unknown>>;
        };
      }
    ).__effluxPlatform;
    if (!platformService?.getDeviceInfo) return null;

    const info = await platformService.getDeviceInfo();
    if (!info || typeof info !== 'object') return null;

    // Serializa só os campos que o server aceita — a shape do DeviceInfo TS
    // é ligeiramente diferente do DTO do backend, mas os campos batem por nome.
    const battery = info.battery as
      | { level?: number; isCharging?: boolean }
      | undefined;
    return {
      platform: info.platform as string | undefined,
      platformVersion: info.platformVersion as string | undefined,
      model: info.model as string | undefined,
      manufacturer: info.manufacturer as string | undefined,
      screenResolution: info.screenResolution as string | undefined,
      ipAddress: info.ipAddress as string | undefined,
      networkName: info.networkName as string | undefined,
      timezone: info.timezone as string | undefined,
      orientation: info.orientation as string | undefined,
      // Enriquecido aqui (ponto único) em vez de nas 5 implementações de
      // plataforma. `extra` já é campo livre no DTO do servidor, então não
      // exige migration nem mudança de contrato.
      // 🔴 NÃO acrescente aqui nada que MUDE ao longo do tempo. Este objeto é
      // resolvido UMA vez e reenviado igual em todo sync (vide
      // `deviceInfoCached`). O snapshot de saúde morava aqui e por isso toda
      // amostra gravada era uma cópia da medição de boot — a série virou uma
      // linha reta. Métrica viva entra em `montarExtraDoSync`, que roda a cada
      // envio.
      extra: {
        ...((info.extra as Record<string, unknown> | undefined) ?? {}),
        ...detectEngineVersion(),
      },
      battery: battery
        ? { level: battery.level, isCharging: battery.isCharging }
        : undefined,
    };
  }

  /**
   * O `device_info` que vai NESTE sync: a parte estática do cache, mais as
   * métricas vivas montadas agora.
   *
   * <p>Separar as duas é o ponto. O `deviceInfoCached` é resolvido uma vez
   * porque `getDeviceInfo` é caro (no Tizen são várias webapis nativas por
   * chamada), e continua assim. Mas o que MUDA não pode morar lá dentro: o
   * snapshot de saúde morava, e como o objeto era reenviado idêntico em todo
   * sync, toda amostra gravada era uma cópia da medição de boot. O tablet do
   * Bruno gerou 8 amostras com UM valor distinto — série virou linha reta,
   * semáforo nunca mudaria de cor, e a janela de 15 min nunca era consultada
   * porque `snapshotDeSaude` só rodava no primeiro sync.</p>
   *
   * <p>Custo: um spread raso por sync. O caro (`getDeviceInfo`) segue cacheado.</p>
   */
  private async montarDeviceInfoDoSync(): Promise<
    SyncRequest['device_info'] | undefined
  > {
    if (!this.deviceInfoCached) return undefined;
    const vivo = await this.snapshotDeSaude();
    // Sem métrica nova (janela fechada) → devolve o cache intacto, sem alocar.
    if (Object.keys(vivo).length === 0) return this.deviceInfoCached;
    return {
      ...this.deviceInfoCached,
      extra: { ...(this.deviceInfoCached.extra ?? {}), ...vivo },
    };
  }

  /**
   * Snapshot de saúde para o histórico (Saúde do Player, Fase 2).
   *
   * <p>Pega carona no sync — sem requisição nova, sem canal novo. É o RESUMO,
   * não o `health:report` inteiro: aquele carrega blackbox do AVPlay e logs, é
   * pesado e existe para o caminho PULL, sob demanda. Aqui vão poucos números,
   * de tempos em tempos, para a série mostrar a degradação NASCER.</p>
   *
   * <p>A janela de 15 min é o que separa "série histórica" de "escrever no banco
   * a cada sync": o sync roda a cada 60 s mesmo com SSE conectado, o que daria
   * 1.440 linhas/dia/player em vez das 96 previstas. O servidor tem a mesma
   * guarda, mas aqui ela também economiza a coleta.</p>
   *
   * <p>Best-effort absoluto: qualquer falha devolve `{}` e o sync segue. É
   * telemetria — não pode custar conteúdo na tela.</p>
   */
  private async snapshotDeSaude(): Promise<Record<string, unknown>> {
    const agora = Date.now();
    if (agora - this.ultimoSnapshotSaude < HEALTH_SAMPLE_INTERVAL_MS) {
      return {};
    }

    try {
      const { records, readMs, loopLagMs } =
        await analyticsDB.getHealthMetrics();
      const up = getUptime();
      // Os três são leitura de coisa JÁ coletada — índice em memória, contador
      // em memória, duas chamadas síncronas do Tizen. Nada de I/O de disco aqui:
      // isto roda no caminho do sync, e o sync existe pra pôr conteúdo na tela.
      const cache = coletarCacheDeMidia();
      const mem = await coletarMemoriaDoSistema();
      this.ultimoSnapshotSaude = agora;
      return {
        health: {
          idb_records: records,
          idb_read_ms: readMs,
          idb_loop_lag_ms: loopLagMs,
          uptime_page_ms: up.pageMs,
          uptime_process_ms: up.processMs,
          app_version: import.meta.env.VITE_APP_VERSION ?? null,
          // Quanto da programação está em disco — prevê tela preta na próxima
          // queda de link, em vez de explicá-la depois. `cache_items` é o que
          // chegou ao disco; `cache_total` é o que o último sync mandou baixar.
          cache_items: cache.files,
          cache_mb: cache.mb,
          cache_total: this.ultimoTotalDeMidia,
          // Canal que morreu sem o EventSource perceber. Acumulado desde o boot,
          // então anda junto com o uptime da mesma amostra.
          sse_zombie: lerSseZumbis(),
          // Casa com o painel de uptime: confirma se a queda foi o refresh que o
          // próprio player agendou, ou algo que ninguém pediu.
          last_reload_at: this.ultimoReloadRegistrado(),
          // Tizen: RAM do SISTEMA, não heap JS — no vazamento do embedded o heap
          // ficou plano até o processo morrer. Null nas outras plataformas.
          sys_mem_free_mb: mem.freeMb,
          sys_mem_total_mb: mem.totalMb,
          // Versão do NATIVO (AAR), que tem ciclo próprio: o bundle atualiza por OTA,
          // o AAR só quando o parceiro recompila. `null` = fora do embedded OU AAR
          // anterior ao 0.4.0 — nos dois casos é informação, não lacuna.
          aar_version: hostAarVersion(),
          // Quantos decoders de vídeo o motor está usando AGORA: 2 com o A/B ligado, 1 sem
          // ele. `null` onde o A/B não se aplica (TV sob AVPlay). É o que diz se a sonda do
          // 2º decoder aprovou ESTE aparelho — vide `useSegundoDecoder`.
          video_decoders: lerDecodersDeVideoEmUso(),
        },
      };
    } catch {
      // Não marca `ultimoSnapshotSaude`: falha transitória não pode calar o
      // player por 15 min.
      return {};
    }
  }

  /**
   * Instante do último reload periódico que o player registrou, ou null.
   *
   * <p>Mesma chave que o `health:report` lê. Valor ausente ou ilegível vira
   * null — um `0` aqui seria lido como "reload em 1970".</p>
   */
  private ultimoReloadRegistrado(): number | null {
    try {
      const bruto = window.localStorage?.getItem(LAST_RELOAD_AT_KEY);
      const n = bruto ? Number(bruto) : NaN;
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }

  // ===========================================================================
  // SSE INTEGRATION
  // ===========================================================================

  private initSSE(): void {
    console.log('[SyncService] Initializing SSE connection', {
      sseApiUrl: this.config.sseApiUrl,
      deviceId: this.config.deviceId,
      sseEnabled: this.config.sseEnabled,
    });

    // Epic D (Fase D2) — em modo SSE o conteúdo chega pelo EventSource do
    // RemoteOps (via unifiedContentChannel); NÃO abrimos uma 2ª conexão. Modo
    // polling segue por HTTP (ContentPollingService) e o canal unificado carrega
    // só comandos.
    if (this.shouldUseUnifiedContent()) {
      this.subscribeUnifiedContent();
      return;
    }

    // O mecanismo inicial vem de VITE_CONTENT_DISCOVERY (default 'sse'); a
    // partir do primeiro sync quem manda é o servidor, via discovery_mode —
    // ver applyDiscoveryMode.
    this.sseService = createContentService({
      apiUrl: this.config.sseApiUrl,
      deviceId: this.config.deviceId,
      tenantId: this.config.tenantId,
      mode: this.discoveryMode,
    });

    // Handle SSE state changes
    this.sseService.onStateChange(this.handleSSEStateChange);

    // Handle content change events. O handler é COMPARTILHADO com o canal
    // unificado (Epic D) — ver handleContentServiceEvent.
    this.sseService.onContentChange(this.handleContentServiceEvent);

    // Connect to SSE
    this.sseService.connect();
  }

  /**
   * `true` quando o conteúdo deve chegar pelo canal SSE UNIFICADO (Epic D): modo
   * de descoberta = SSE. Nesse modo o conteúdo vem pelo EventSource do RemoteOps
   * (canal unificado) — sem 2ª conexão. Em polling, o conteúdo vem por HTTP
   * (ContentPollingService) e o canal unificado carrega só comandos.
   */
  private shouldUseUnifiedContent(): boolean {
    return this.discoveryMode === 'sse';
  }

  /**
   * Liga o conteúdo ao canal SSE unificado (RemoteOps) em vez de abrir uma 2ª
   * conexão. Idempotente. O canal do RemoteOps está sempre ligado (comandos),
   * então tratamos o conteúdo como "SSE conectado" pro safety-net polling —
   * mesmo steady-state do modo SSE legado, mas com UMA conexão.
   */
  private subscribeUnifiedContent(): void {
    if (this.unifiedContentUnsub) return; // já inscrito
    console.log(
      '[SyncService] 🔗 Conteúdo pelo canal SSE unificado (RemoteOps) — sem 2ª conexão',
    );
    this.unifiedContentUnsub = unifiedContentChannel.onContentEvent(
      this.handleContentServiceEvent,
    );
    this.sseConnected = true;
    this.adjustPollingInterval(this.config.sseConnectedPollingIntervalMs);
  }

  /** Cancela a inscrição no canal unificado (no-op se não inscrito). */
  private unsubscribeUnifiedContent(): void {
    if (this.unifiedContentUnsub) {
      this.unifiedContentUnsub();
      this.unifiedContentUnsub = null;
    }
  }

  /**
   * Handler ÚNICO de evento de conteúdo — reusado pelo Content SSE (modo SSE
   * legado / flag OFF) E pelo canal unificado (Epic D, Fase D2). Garante que um
   * `content_changed` que chega pelo canal unificado dispara EXATAMENTE o mesmo
   * comportamento que disparava pela 2ª conexão do Content SSE.
   */
  private handleContentServiceEvent = (event: SSEEvent): void => {
    console.log(
      '[SyncService] 🔔 SSE content change event received:',
      event.type,
      event,
    );

    const payload = (event.data || {}) as Record<string, unknown>;
    const eventDeviceId =
      (typeof payload.device_id === 'string' && payload.device_id) ||
      (typeof payload.hardwareId === 'string' && payload.hardwareId) ||
      (typeof payload.hardware_id === 'string' && payload.hardware_id) ||
      null;

    // 2026-06-08 — Eventos com device_id explícito são targeted (ex:
    // orientation_changed do ANDROID-xxx). TV Tizen não deve sync+prefetch
    // por mudança de outro player no mesmo tenant — causava crash loop no
    // boot (Renato: v1.0.157 caiu 2× em <1min com CocacolaEscolhas saving).
    if (
      eventDeviceId &&
      eventDeviceId !== this.config.deviceId &&
      (event.type === 'content_changed' ||
        event.type === 'player_orientation_changed')
    ) {
      console.log(
        `[SyncService] ⏭️ Ignoring ${event.type} for other device (${eventDeviceId})`,
      );
      return;
    }

    // BATIDA DE SINCRONISMO (videowall). O servidor anuncia a hora canônica pelo canal
    // que já está aberto. Vale mais que a estimativa por round-trip do sync: chega pronta,
    // e a cada 15s em vez de só quando há sync. NÃO é uma barreira — ninguém espera
    // ninguém; quem não recebe segue com o cálculo local e não atrapalha os outros.
    // Só chega a player de grupo exclusivo (o servidor filtra o destino).
    if (event.type === 'sync_beat') {
      // Tratada por `handleSyncBeat` (inscrição dedicada, sempre ativa). Aqui só sai.
      return;
    }

    // Handle trigger events
    if (event.type === 'trigger_fired') {
      console.log('[SyncService] 🎯 Trigger fired event:', event.data);
      this.notifyTriggerFired(event.data);
      return;
    }

    if (event.type === 'trigger_deactivated') {
      console.log('[SyncService] ⏹️ Trigger deactivated event:', event.data);
      this.notifyTriggerDeactivated(event.data);
      return;
    }

    if (event.type === 'config_update') {
      console.log('[SyncService] 🔔 Config update received:', event.data);
      const payload = event.data || {};
      console.log(
        '[SyncService] 🔔 Config update payload keys:',
        Object.keys(payload),
      );
      const pick = <T>(camelKey: string, snakeKey: string): T | undefined => {
        const camel = (payload as Record<string, T>)[camelKey];
        if (camel !== undefined) return camel;
        return (payload as Record<string, T>)[snakeKey];
      };
      this.notifyConfigUpdate({
        playerId: pick<string>('playerId', 'player_id'),
        volumeLevel: pick<number>('volumeLevel', 'volume_level'),
        resetVolumeTimeoutSeconds: pick<number>(
          'resetVolumeTimeoutSeconds',
          'reset_volume_timeout_seconds',
        ),
        batteryWarningEnabled: pick<boolean>(
          'batteryWarningEnabled',
          'battery_warning_enabled',
        ),
        audioAlertEnabled: pick<boolean>(
          'audioAlertEnabled',
          'audio_alert_enabled',
        ),
        audioAlertVolumeLevel: pick<number>(
          'audioAlertVolumeLevel',
          'audio_alert_volume_level',
        ),
        audioAlertBeepCount: pick<number>(
          'audioAlertBeepCount',
          'audio_alert_beep_count',
        ),
        audioAlertIntervalMinutes: pick<number>(
          'audioAlertIntervalMinutes',
          'audio_alert_interval_minutes',
        ),
        audioAlertLowBatteryBeepCount: pick<number>(
          'audioAlertLowBatteryBeepCount',
          'audio_alert_low_battery_beep_count',
        ),
        audioAlertLowBatteryIntervalMinutes: pick<number>(
          'audioAlertLowBatteryIntervalMinutes',
          'audio_alert_low_battery_interval_minutes',
        ),
        visualAlertEnabled: pick<boolean>(
          'visualAlertEnabled',
          'visual_alert_enabled',
        ),
        visualAlertBlinkCount: pick<number>(
          'visualAlertBlinkCount',
          'visual_alert_blink_count',
        ),
        visualAlertDurationSeconds: pick<number>(
          'visualAlertDurationSeconds',
          'visual_alert_duration_seconds',
        ),
        visualAlertLowBatteryBlinkCount: pick<number>(
          'visualAlertLowBatteryBlinkCount',
          'visual_alert_low_battery_blink_count',
        ),
        visualAlertLowBatteryDurationSeconds: pick<number>(
          'visualAlertLowBatteryDurationSeconds',
          'visual_alert_low_battery_duration_seconds',
        ),
        backgroundPortraitUrl: pick<string>(
          'backgroundPortraitUrl',
          'background_portrait_url',
        ),
        backgroundLandscapeUrl: pick<string>(
          'backgroundLandscapeUrl',
          'background_landscape_url',
        ),
        kioskModeEnabled: pick<boolean>(
          'kioskModeEnabled',
          'kiosk_mode_enabled',
        ),
        kioskUnlockPinHashes: pick<string[]>(
          'kioskUnlockPinHashes',
          'kiosk_unlock_pin_hashes',
        ),
        // V046 — brilho. Backend manda em snake_case (Jackson SNAKE_CASE
        // global). Sem este mapping, ConfigManager.applyBrightness recebia
        // brightnessAuto=undefined e caía em setAuto() — bug do "Brilho:
        // N/A" no tablet/TV mesmo após config no admin.
        brightnessAuto: pick<boolean>('brightnessAuto', 'brightness_auto'),
        brightnessLevel: pick<number>('brightnessLevel', 'brightness_level'),
      });
      return;
    }

    if (event.type === 'restart_command') {
      // PLAYER_RESTART_PLAN — admin disparou restart remoto
      console.log('[SyncService] 🔄 Restart command received:', event.data);
      this.notifyRestartCommand(event.data || {});
      return;
    }

    if (event.type === 'sync_correction') {
      // IMPULSO DO SUPERVISOR: a hora que veio é uma amostra de relógio (como a batida), e
      // a store realinha a posição pela época. NÃO é re-sync de conteúdo.
      const payload = (event.data || {}) as SyncCorrectionPayload;
      console.log(
        '[SyncService] 🧭 Impulso de correção de sincronismo:',
        payload,
      );
      if (typeof payload.server_time === 'string') {
        applyServerTime(payload.server_time);
      }
      this.notifySyncCorrection(payload);
      return;
    }

    if (event.type === 'player_orientation_changed') {
      // Admin trocou paisagem ↔ retrato pelo dropdown. Server emite via
      // PlayerService.updatePlayer (afterCommit). Aqui só normalizamos o
      // payload e delegamos pro listener — ele aplica override no
      // authStore + forceResync pra trocar conteúdo da nova orientação.
      const payload = (event.data || {}) as Record<string, unknown>;
      const orientation = payload.orientation;
      if (orientation === 'landscape' || orientation === 'portrait') {
        console.log(
          '[SyncService] 🔁 Orientation change from admin:',
          orientation,
          '(previous:',
          payload.previousOrientation,
          ')',
        );
        this.notifyOrientationChangeFromAdmin(orientation);
      } else if (typeof orientation === 'string' && orientation.length > 0) {
        console.log(
          '[SyncService] 🔁 Custom orientation change from admin:',
          orientation,
          '— triggering resync to pick up new layout',
        );
        this.forceResync();
      } else {
        console.warn(
          '[SyncService] player_orientation_changed com orientation inválida:',
          orientation,
        );
      }
      return;
    }

    // Clear hash to force full resync (content changed on server)
    console.log(
      '[SyncService] 🗑️ Clearing library hash due to content change (was:',
      this.libraryHash?.substring(0, 16) + '...)',
    );
    this.libraryHash = null;

    // Fase 1a — anti-thundering-herd. Um content_changed de BROADCAST (sem
    // device_id) chega a TODA a frota ao mesmo tempo; sincronizar na hora faz
    // 6000 players baterem no rebuild pesado do server simultaneamente. Aplica
    // jitter só no broadcast. Evento TARGETED (device_id == este player) é 1
    // player só → sincroniza imediato (responsivo, sem herd).
    // O servidor manda `sync_after_seconds` quando conhece o tamanho da
    // frota — ele sabe quantas telas há e dimensiona a janela por isso, e dá
    // para alargá-la em produção sem republicar o player. Sem o campo
    // (servidor antigo), vale o jitter local, que já distingue broadcast de
    // targeted.
    const doServidor = event.data?.syncAfterSeconds;
    const delayMs =
      typeof doServidor === 'number' && doServidor > 0
        ? doServidor * 1000
        : computeContentResyncDelayMs(eventDeviceId, this.config.deviceId);
    if (delayMs === 0) {
      console.log('[SyncService] 🚀 Targeted content change → immediate sync');
      this.sync();
    } else {
      console.log(
        `[SyncService] 🚀 Broadcast content change → resync com jitter de ${delayMs}ms (anti-thundering-herd)`,
      );
      setTimeout(() => this.sync(), delayMs);
    }
  };

  /**
   * Agenda o sync depois de uma mudança, respeitando o atraso que o servidor
   * mandou.
   *
   * <p><b>Por que não sincronizar na hora.</b> Quando o conteúdo muda, todas as
   * telas do tenant descobrem dentro de uma janela de segundos. Sincronizar
   * imediatamente faz 15.000 delas caírem no banco juntas — o sync segura uma
   * conexão do pool pela requisição inteira, e o servidor satura em ~450/s
   * (medido). O teste de herd de 2026-08-06 mediu essa sincronia custando 0,77
   * conexão de fila por tela.</p>
   *
   * <p>O atraso é sorteado <b>no servidor</b>, dentro de uma janela
   * configurável. O player não sorteia nada — assim dá para alargar a janela em
   * produção sem republicar o player.</p>
   *
   * <p>Sem o campo (stream aberto, servidor antigo), sincroniza na hora: é o
   * comportamento de sempre, e nesse caminho a frota é pequena o bastante para
   * o herd não importar.</p>
   */
  private scheduleSyncAfterChange(syncAfterSeconds?: number): void {
    if (typeof syncAfterSeconds !== 'number' || syncAfterSeconds <= 0) {
      console.log('[SyncService] 🚀 Sync imediato (sem atraso do servidor)');
      this.sync();
      return;
    }

    if (this.pendingSyncTimer) {
      clearTimeout(this.pendingSyncTimer);
    }
    console.log(
      `[SyncService] ⏳ Sync agendado para daqui a ${syncAfterSeconds}s (jitter do servidor)`,
    );
    this.pendingSyncTimer = setTimeout(() => {
      this.pendingSyncTimer = null;
      this.sync();
    }, syncAfterSeconds * 1000);
  }

  /**
   * "Descobri que mudou, sincroniza" vindo de FORA do serviço de descoberta.
   *
   * <p>Host na Fase 1: o ping de liveness de 30 s (`ConnectionMonitorService`),
   * que passou a carregar `needs_sync`. Um request só faz prova de vida E
   * frescor — e quando o excedente da frota não cabe no SSE, é por aqui que ele
   * descobre a mudança, sem uma 2ª conexão persistente.</p>
   *
   * <p>Faz EXATAMENTE o que um `content_changed` do poller faz: zera o hash
   * (força full resync) e agenda respeitando o `sync_after_seconds` do servidor
   * (anti-thundering-herd). É idempotente com o poller/SSE — se dois caminhos
   * virem a mesma mudança, o 2º sync sai barato (hash igual → "no update
   * required").</p>
   */
  requestContentSync(syncAfterSeconds?: number): void {
    this.libraryHash = null;
    this.scheduleSyncAfterChange(syncAfterSeconds);
  }

  /**
   * Aplica a batida de sincronismo ao relógio. Deduplica pela hora anunciada: o mesmo
   * evento pode chegar por mais de um caminho, e contar duas vezes inflaria o quórum
   * que protege o relógio contra batidas atrasadas.
   */
  private handleSyncBeat = (event: SSEEvent): void => {
    if (event.type !== 'sync_beat') return;
    const beat = (event.data || {}) as Record<string, unknown>;
    if (typeof beat.server_time !== 'string') return;
    if (beat.server_time === this.lastBeatKey) return;
    this.lastBeatKey = beat.server_time;
    applyServerTime(beat.server_time);
  };

  /**
   * Religa os callbacks no serviço de descoberta atual.
   *
   * <p>Chamado ao trocar de mecanismo: o serviço novo nasce sem nenhum
   * assinante, e sem religar isto a tela pararia de reagir a mudanças
   * silenciosamente — o pior modo de falha possível, porque ela continua
   * exibindo conteúdo, só que velho.</p>
   */
  private wireContentServiceCallbacks(): void {
    if (!this.sseService) {
      return;
    }
    const svc = this.sseService;
    svc.onStateChange(this.handleSSEStateChange);
    svc.onContentChange((event) => {
      if (event.type === 'trigger_fired') {
        this.notifyTriggerFired(event.data);
        return;
      }
      if (event.type === 'trigger_deactivated') {
        this.notifyTriggerDeactivated(event.data);
        return;
      }
      // A batida de sincronismo NÃO é mudança de conteúdo: só acerta o relógio.
      // Sem esta saída ela cairia no invalidate abaixo e cada player de videowall
      // faria um sync COMPLETO a cada 15s — a batida custaria mais que o problema
      // que resolve. Quem trata a batida é o `handleContentServiceEvent`.
      if (event.type === 'sync_beat') {
        return;
      }
      this.libraryHash = null;
      this.scheduleSyncAfterChange(event.data?.syncAfterSeconds);
    });
  }

  private handleSSEStateChange = (state: SSEConnectionState): void => {
    console.log('[SyncService] SSE state changed:', state);

    const wasConnected = this.sseConnected;
    this.sseConnected = state === 'connected';

    // Adjust polling interval based on SSE state
    if (this.sseConnected && !wasConnected) {
      // SSE connected - reduce polling frequency
      this.adjustPollingInterval(this.config.sseConnectedPollingIntervalMs);
    } else if (!this.sseConnected && wasConnected) {
      // SSE disconnected - restore normal polling frequency
      this.adjustPollingInterval(this.config.pollingIntervalMs);
      // Trigger immediate sync since we may have missed events
      this.sync();
    }

    // FIX (deep-review #15 P3): SSE circuit_broken — após N tentativas de
    // reconnect sem sucesso, o ContentSSEService desiste. Polling fica como
    // único caminho de sync. Garante que o intervalo está no "fallback
    // agressivo" (pollingIntervalMs, não o reduzido sseConnectedPollingIntervalMs)
    // e dispara um sync imediato pra recuperar o gap. Reset do circuit ocorre
    // via ensureSSEConnected(), online event ou token refresh.
    if (state === 'circuit_broken') {
      console.warn(
        '[SyncService] SSE circuit broken — falling back to aggressive polling.',
      );
      this.adjustPollingInterval(this.config.pollingIntervalMs);
      this.sync();
    }
  };

  private adjustPollingInterval(newIntervalMs: number): void {
    if (this.currentPollingIntervalMs === newIntervalMs) return;

    console.debug(
      `[SyncService] Adjusting polling interval from ${this.currentPollingIntervalMs / 1000}s to ${newIntervalMs / 1000}s`,
    );

    this.currentPollingIntervalMs = newIntervalMs;

    // Restart polling with new interval if currently polling
    if (this.pollingInterval) {
      this.stopPolling();
      this.startPolling(newIntervalMs);
    }
  }

  // ===========================================================================
  // PUBLIC METHODS
  // ===========================================================================

  /**
   * Checks if the device ID is temporary.
   */
  private isTemporaryId(id: string): boolean {
    return !id || id.startsWith('efflux-temp-') || id.startsWith('DEV-');
  }

  /**
   * 2026-05-24 — Sanitiza headers pra log diagnostic. Tokens (X-Device-Token,
   * Authorization) viram length-only pra evitar vazar credenciais no console
   * remoto / LogRocket / chrome inspect mas ainda permitir checar se o
   * header está sendo enviado.
   */
  // 2026-05-24 fix: declarado como arrow property (não method) pra preservar
  // `this` quando chamado de dentro dos interceptors do axios. O transpile
  // de regular methods perdia o binding ("_this.redactHeadersForLog is not a
  // function" no console do browser).
  private redactHeadersForLog = (
    headers: unknown,
  ): Record<string, string | number | boolean | undefined> => {
    const out: Record<string, string | number | boolean | undefined> = {};
    if (!headers || typeof headers !== 'object') return out;
    const SENSITIVE = new Set([
      'authorization',
      'x-device-token',
      'cookie',
      'set-cookie',
      'x-api-key',
    ]);
    // Axios headers pode ser AxiosHeaders instance ou plain object.
    // Iteramos via for..in (compatível com ambos) + Object.entries fallback.
    try {
      // AxiosHeaders has a toJSON method in axios 1.x
      const maybeToJSON = (headers as { toJSON?: () => unknown }).toJSON;
      const plain =
        typeof maybeToJSON === 'function'
          ? (maybeToJSON.call(headers) as Record<string, unknown>)
          : (headers as Record<string, unknown>);
      for (const [key, value] of Object.entries(plain)) {
        const lower = key.toLowerCase();
        if (SENSITIVE.has(lower)) {
          out[key] =
            typeof value === 'string' ? `<len=${value.length}>` : '<set>';
        } else if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          out[key] = value;
        } else if (value !== undefined && value !== null) {
          out[key] = String(value);
        }
      }
    } catch (e) {
      out._headerSerializationError = String(e);
    }
    return out;
  };

  /**
   * 2026-05-24 — Sumariza response headers em formato logável.
   * Importante manter Access-Control-Allow-* visível pra diagnosticar CORS.
   *
   * 2026-05-24 fix: arrow property (mesmo motivo do redactHeadersForLog).
   */
  private summarizeResponseHeaders = (
    headers: unknown,
  ): Record<string, string | undefined> => {
    const out: Record<string, string | undefined> = {};
    if (!headers || typeof headers !== 'object') return out;
    const RELEVANT_LOWER = [
      'content-type',
      'content-length',
      'access-control-allow-origin',
      'access-control-allow-methods',
      'access-control-allow-headers',
      'access-control-allow-credentials',
      'access-control-expose-headers',
      'x-ratelimit-remaining',
      'x-tenant-id',
      'server',
      'date',
    ];
    try {
      const maybeToJSON = (headers as { toJSON?: () => unknown }).toJSON;
      const plain =
        typeof maybeToJSON === 'function'
          ? (maybeToJSON.call(headers) as Record<string, unknown>)
          : (headers as Record<string, unknown>);
      for (const [key, value] of Object.entries(plain)) {
        if (RELEVANT_LOWER.includes(key.toLowerCase())) {
          out[key] = typeof value === 'string' ? value : String(value);
        }
      }
    } catch (e) {
      out._headerSerializationError = String(e);
    }
    return out;
  };

  async sync(): Promise<SyncResult> {
    // Sync acontece frequente (boot + SSE events + polling) — debug
    console.debug(
      '[SyncService] Initializing sync request. Device:',
      this.config.deviceId,
    );

    // Block sync if using temporary ID to prevent database pollution
    if (this.isTemporaryId(this.config.deviceId)) {
      console.log(
        `[SyncService] Skipping sync - temporary Device ID (${this.config.deviceId}). Waiting for valid auth.`,
      );
      return {
        success: false,
        error: 'Temporary Device ID',
      };
    }

    if (!this.isOnline()) {
      console.log('[SyncService] Device is offline, skipping sync');
      return {
        success: false,
        error: 'Device is offline',
      };
    }

    try {
      // Resolve backend de kiosk uma vez (cache subsequente é instantâneo)
      if (this.kioskBackendCached === null) {
        try {
          const info = await getNativeKioskBackend();
          this.kioskBackendCached = info?.name ?? 'unknown';
        } catch {
          this.kioskBackendCached = 'unknown';
        }
      }

      // Resolve deviceInfo uma vez (cache subsequente é instantâneo). Enviado
      // pro server hidratar tab_players.metadata — vide docstring de
      // `deviceInfoCached` acima. Best-effort: falha aqui NÃO bloqueia o sync.
      if (this.deviceInfoCached === null) {
        try {
          this.deviceInfoCached = await this.resolveDeviceInfo();
        } catch (err) {
          console.warn(
            '[SyncService] resolveDeviceInfo failed (best-effort):',
            err,
          );
        }
      }

      const request: SyncRequest = {
        device_id: this.config.deviceId,
        client_version: this.config.clientVersion,
        old_timestamp: this.lastSyncTime || undefined,
        current_library_hash: this.libraryHash || undefined,
        kiosk_enabled: kioskStore.isLocked,
        kiosk_backend: this.kioskBackendCached,
        // O estático vem do cache; o vivo é montado agora (vide
        // `montarDeviceInfoDoSync`).
        device_info: await this.montarDeviceInfoDoSync(),
        // Host embedded (Fase 4a): só viaja quando o host já setou tags ao menos
        // uma vez (hostTags !== null). Player normal → undefined → chave some do
        // JSON → server no-op. Vazio ([]) é enviado de propósito (host limpou).
        tags: this.hostTags ?? undefined,
        // Host-owned volume (embedded, Parte B — visibilidade). Só viaja quando o
        // host já assumiu o volume ao menos uma vez (hasHostVolumeState). Player
        // normal → chaves ausentes → server no-op. Reporta o estado ATUAL
        // (controlled true/false) pra o admin ligar/desligar o badge; o nível só
        // acompanha quando o host está no controle.
        ...(hasHostVolumeState()
          ? {
              host_volume_controlled: isHostVolumeControlled(),
              host_volume_level: getHostVolumeLevel() ?? undefined,
            }
          : {}),
      };

      // Cronometra o round-trip pra estimar o relógio do servidor (vide serverClock).
      // Sem isso, a fórmula do sync de videowall usa o relógio do device e a
      // diferença entre aparelhos vira defasagem permanente na tela.
      const syncSentAt = Date.now();
      const response = await this.api.post<SyncResponse>(
        SYNC_ENDPOINT,
        request,
      );

      const data = response.data;

      recordSyncSample(data?.sync_timestamp, syncSentAt, Date.now());

      // Denominador do "N de M em disco" da série de saúde. Vem DAQUI, e não do
      // `mediaStore`, porque este serviço não pode importar `~/stores` —
      // `~/stores` instancia o `syncStore`, que importa este arquivo de volta, e
      // o ciclo entrega `undefined` em runtime sem falhar o build. Um sync
      // `no-update` não traz `files`; nesse caso o total anterior continua
      // valendo, que é o fato (a programação não mudou).
      if (Array.isArray(data?.files) && data.files.length > 0) {
        this.ultimoTotalDeMidia = data.files.length;
      }

      if (
        !data?.files ||
        !Array.isArray(data.files) ||
        data.files.length === 0
      ) {
        if (data.update_required !== 'no') {
          console.warn(`[SyncService] ⚠️ No files in response`);
        }
      }

      // Reset retry count on success
      this.retryCount = 0;

      // Check if update is required
      if (data.update_required === 'no') {
        console.log('[SyncService] Library is up to date');

        // Update timestamp even if no content changed
        this.lastSyncTime = data.sync_timestamp;
        // libraryHash remains the same (verified by server)

        // V066 — Mesmo em no-update, propaga playerSchedule (e playerGroups).
        // Schedule do player pode mudar sem mexer em library_hash (mudança de
        // janela ≠ mudança de conteúdo). Sem isto, cliente ficaria com
        // schedule estagnado até alguma campanha mudar. transformSyncResponse
        // sem files retorna shape vazio + esses 2 campos preservados.
        const noUpdateTransform = transformSyncResponse(data);

        const result: SyncResult = {
          success: true,
          timestamp: data.sync_timestamp,
          libraryHash: data.library_hash,
          playlistItems: [], // No items returned
          playerGroups: noUpdateTransform.playerGroups, // V060
          playerSchedule: noUpdateTransform.playerSchedule ?? null, // V066
          playlistSchedules: noUpdateTransform.playlistSchedules, // V067
          kioskModeEnabled: data.kiosk_mode_enabled,
          discoveryMode: data.discovery_mode,
          playerStatus: data.player_status,
          // 2026-06-18 — paridade com V066: slot_sequence vem TAMBEM em
          // update_required=no (server reinjeta em hash short-circuit, e o
          // fluxo completo retorna 'no' quando hashes batem). Sem essa
          // propagacao, sync.store.ts:1014 recebia undefined -> setFromSync(null)
          // -> playbackStore reseta currentSlotSequence -> player cai pra Tier 4
          // legacy (5 items, sem distribuicao harmonica) -> viola R1 do
          // loop-reproducao-player.md. Bug reportado pelo Renato em prod
          // (Tablet Renato v1.0.192 reportando tier=4 enquanto admin via tier=3).
          slotSequence: data.slot_sequence ?? null,
          orientacaoId: data.orientacao_id,
          orientationWidth: data.orientation_width,
          orientationHeight: data.orientation_height,
          screenOffsetX: data.screen_offset_x,
          screenOffsetY: data.screen_offset_y,
          zones: Array.isArray(data.zones)
            ? (data.zones as unknown as RawZonePayload[]).map((z) => ({
                id: z.id as number,
                name: (z.name ?? '') as string,
                posX: (z.pos_x ?? z.posX ?? 0) as number,
                posY: (z.pos_y ?? z.posY ?? 0) as number,
                width: (z.width ?? 0) as number,
                height: (z.height ?? 0) as number,
                zOrder: (z.z_order ?? z.zOrder ?? 0) as number,
                layoutWidth: (z.layout_width ?? z.layoutWidth) as
                  | number
                  | undefined,
                layoutHeight: (z.layout_height ?? z.layoutHeight) as
                  | number
                  | undefined,
              }))
            : data.zones,
          availableOrientations: data.available_orientations,
        };

        this.notifySyncComplete(result);
        return result;
      }

      // Transform response to client models
      let transformResult;
      try {
        transformResult = transformSyncResponse(data);
      } catch (error) {
        console.error(`[SyncService] ❌ transformSyncResponse FAILED:`, error);
        throw error;
      }

      // Fase 1.0/1.5 — alimenta o weatherStore com o clima do sync (offline-first;
      // se data.weather vier ausente, mantém o último conhecido).
      weatherStore.setFromSync(data.weather);

      const {
        campaigns,
        allPlaylistItems,
        triggers,
        playerGroups,
        playerSchedule,
        playlistSchedules,
      } = transformResult;

      // Update internal state
      this.lastSyncTime = data.sync_timestamp;
      this.libraryHash = data.library_hash;

      const result: SyncResult = {
        success: true,
        campaigns: campaigns || [],
        playlistItems: allPlaylistItems || [],
        triggers: triggers || [],
        playerGroups: playerGroups || [], // V060 — 4ª camada AND
        playerSchedule: playerSchedule ?? null, // V066 — 5ª camada AND
        playlistSchedules: playlistSchedules || [], // V067 — 6ª camada AND
        libraryHash: data.library_hash,
        timestamp: data.sync_timestamp,
        kioskModeEnabled: data.kiosk_mode_enabled,
        discoveryMode: data.discovery_mode,
        playerStatus: data.player_status,
        slotSequence: data.slot_sequence ?? null, // Sprint 2 CP7
        orientacaoId: data.orientacao_id,
        orientationWidth: data.orientation_width,
        orientationHeight: data.orientation_height,
        screenOffsetX: data.screen_offset_x,
        screenOffsetY: data.screen_offset_y,
        zones: Array.isArray(data.zones)
          ? (data.zones as unknown as RawZonePayload[]).map((z) => ({
              id: z.id as number,
              name: (z.name ?? '') as string,
              posX: (z.pos_x ?? z.posX ?? 0) as number,
              posY: (z.pos_y ?? z.posY ?? 0) as number,
              width: (z.width ?? 0) as number,
              height: (z.height ?? 0) as number,
              zOrder: (z.z_order ?? z.zOrder ?? 0) as number,
              layoutWidth: (z.layout_width ?? z.layoutWidth) as
                | number
                | undefined,
              layoutHeight: (z.layout_height ?? z.layoutHeight) as
                | number
                | undefined,
            }))
          : data.zones,
        availableOrientations: data.available_orientations,
      };

      // Notify listeners
      this.notifySyncComplete(result);
      // Sprint 2 CP7 — log passivo do payload de Tier 2/3 pra debug.
      // Não dispara nenhuma ação aqui (CP8 store + CP9 loop fazem isso).
      if (data.slot_sequence) {
        console.log(
          `[SyncService] 🎞️ slot_sequence recebida: tier=${data.slot_sequence.tier}, slots=${data.slot_sequence.slots?.length ?? 0}, playlist="${data.slot_sequence.playlist_name}"`,
        );
      }
      console.log(
        `[SyncService] ✅ Sync complete: ${campaigns.length} campaigns, ${allPlaylistItems.length} items, ${triggers?.length || 0} triggers`,
      );

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      // Check if this is an authentication error (401)
      const isAuthError =
        axios.isAxiosError(error) && error.response?.status === 401;

      // Renato 2026-06-12: rebaixa log pra INFO quando device está pending —
      // 401 é esperado nesse estado, não ruído de console vermelho.
      logAuthAwareError('error', '[SyncService]', 'Sync failed:', errorMessage);

      if (isAuthError) {
        logAuthAwareError(
          'warn',
          '[SyncService]',
          'Authentication error - device not approved or not found',
        );
        // Renato 2026-06-12: 401 do sync quando NÃO estamos pending = server
        // revogou nosso token (player deletado / rotacionado). Dispara recovery
        // que limpa playerId e força re-register. Cooldown de 2min no
        // handleServerRevocation evita storm com sse-token+analytics.
        if (!isWaitingDeviceApproval()) {
          triggerAuthRecovery('sync 401');
        }

        const result: SyncResult = {
          success: false,
          error: 'Device not authenticated',
          authError: true,
        };
        this.notifySyncComplete(result);
        return result;
      }

      // Handle retry for non-auth errors
      this.retryCount++;
      if (this.retryCount < this.config.maxRetries) {
        console.log(
          `[SyncService] Will retry in ${this.config.retryDelayMs / 1000}s (attempt ${this.retryCount}/${this.config.maxRetries})`,
        );
        setTimeout(() => this.sync(), this.config.retryDelayMs);
      }

      const result: SyncResult = {
        success: false,
        error: errorMessage,
      };

      this.notifySyncComplete(result);

      return result;
    }
  }

  startPolling(intervalMs?: number): void {
    this.stopPolling();

    // Use provided interval, or current interval (which may be adjusted by SSE)
    const interval = intervalMs ?? this.currentPollingIntervalMs;

    // Execute sync immediately
    this.sync();

    // Set up periodic sync
    this.pollingInterval = setInterval(() => {
      this.sync();
    }, interval);

    console.debug(
      `[SyncService] Polling started (every ${interval / 1000}s, SSE ${this.sseConnected ? 'connected' : 'disconnected'})`,
    );
  }

  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      console.debug('[SyncService] Polling stopped');
    }
  }

  /**
   * Fonte de verdade de conectividade, injetada pelo dono do
   * `ConnectionMonitorService` (o `sync.store`). Ausente = fallback legado.
   */
  private onlineProvider: (() => boolean) | null = null;

  /**
   * Troca a fonte de verdade do [isOnline] por uma probe real.
   *
   * `navigator.onLine` reflete o ConnectivityManager do sistema, que responde
   * "sem rede" sempre que o aparelho não tem interface própria — mesmo quando há
   * rede de verdade chegando por outro caminho. Num tablet cujo tráfego sai por
   * um túnel (USB/`adb reverse`), isso aborta o sync **antes de qualquer
   * requisição**, e o sintoma não aponta para a causa: o `ConnectionMonitor`
   * loga `server=true` na linha seguinte a "Device is offline, skipping sync".
   *
   * O projeto já havia migrado outros consumidores para a probe real
   * (`sync.store`, `Stream`); este era o que faltava.
   *
   * @param provider `null` restaura o fallback legado.
   */
  setOnlineProvider(provider: (() => boolean) | null): void {
    this.onlineProvider = provider;
  }

  isOnline(): boolean {
    const provider = this.onlineProvider;
    if (provider) {
      try {
        return provider();
      } catch {
        // Provider quebrado não pode derrubar o sync — cai no fallback abaixo.
      }
    }
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true; // Assume online in non-browser environments
  }

  onSyncComplete(callback: SyncCallback): () => void {
    this.syncCallbacks.add(callback);
    return () => {
      this.syncCallbacks.delete(callback);
    };
  }

  onConfigUpdate(callback: ConfigUpdateCallback): () => void {
    this.configCallbacks.add(callback);
    return () => {
      this.configCallbacks.delete(callback);
    };
  }

  onConnectivityChange(callback: ConnectivityCallback): () => void {
    this.connectivityCallbacks.add(callback);
    return () => {
      this.connectivityCallbacks.delete(callback);
    };
  }

  ensureSSEConnected(): void {
    if (!this.sseService) return;
    // Se o circuit breaker tripou, ensureConnected() é no-op — precisa
    // resetar pra dar nova chance ao SSE. Disparado tipicamente por
    // online event, token refresh ou ação do admin.
    if (this.sseService.getState() === 'circuit_broken') {
      this.sseService.reset();
      return;
    }
    this.sseService.ensureConnected();
  }

  getLastHeartbeatTime(): number | null {
    return this.sseService ? this.sseService.getLastHeartbeatTime() : null;
  }

  onHeartbeat(callback: (timestamp: number) => void): () => void {
    if (this.sseService) {
      return this.sseService.onHeartbeat(callback);
    }
    // Return no-op unsubscribe if SSE not available
    return () => {};
  }

  getLastSyncTime(): string | null {
    return this.lastSyncTime;
  }

  getLibraryHash(): string | null {
    return this.libraryHash;
  }

  /**
   * @deprecated API key authentication is deprecated. Use device ID authentication instead.
   * This method is kept for backward compatibility but has no effect.
   */
  setApiKey(_apiKey: string): void {
    console.warn(
      '[SyncService] setApiKey is deprecated - authentication uses X-Device-Id header now',
    );
  }

  /**
   * Update the device ID used for authentication.
   * This updates the config, axios headers, and SSE service.
   * Call this when the correct hardware ID is detected after initialization.
   */
  setDeviceId(deviceId: string): void {
    // Skip if unchanged
    if (this.config.deviceId === deviceId) {
      console.log('[SyncService] Device ID unchanged, skipping update');
      return;
    }

    console.log('[SyncService] Updating device ID:', {
      old: this.config.deviceId?.substring(0, 20) + '...',
      new: deviceId?.substring(0, 20) + '...',
    });

    this.config.deviceId = deviceId;

    if (deviceId) {
      this.api.defaults.headers.common['X-Device-Id'] = deviceId;
      this.api.defaults.headers['X-Device-Id'] = deviceId;
    } else {
      delete this.api.defaults.headers.common['X-Device-Id'];
      delete this.api.defaults.headers['X-Device-Id'];
    }

    // Also update SSE service if enabled
    if (this.sseService && deviceId) {
      this.sseService.updateDeviceId(deviceId);
    }
  }

  /**
   * Forces a full re-sync by clearing local state
   */
  async forceResync(): Promise<void> {
    console.log('[SyncService] Forcing full re-sync (clearing local hash)');
    this.libraryHash = null;
    this.lastSyncTime = null;
    await this.sync();
  }

  /**
   * Reporta tentativa de desbloqueio do kiosk pro servidor.
   * NÃO envia o PIN tentado — só success/fail/lockout.
   *
   * Usado tanto pra envio imediato (quando online) quanto pra drenar a fila
   * offline acumulada em {@code kioskStore.auditQueue}.
   *
   * @returns true se o servidor aceitou (status 2xx), false se falhou
   */
  async reportKioskUnlockAttempt(
    playerId: string,
    body: {
      success: boolean;
      lockoutTriggered: boolean;
      lockoutDurationSec: number;
      clientEventId: string;
    },
  ): Promise<boolean> {
    if (!playerId) {
      console.warn(
        '[SyncService] Cannot report unlock attempt: playerId missing',
      );
      return false;
    }
    if (!this.isOnline()) {
      console.log('[SyncService] Offline — unlock attempt stays in queue');
      return false;
    }
    try {
      await this.api.post(
        `/api/players/${playerId}/kiosk-unlock-attempt`,
        body,
      );
      return true;
    } catch (error) {
      console.warn(
        '[SyncService] Failed to report kiosk unlock attempt:',
        error,
      );
      return false;
    }
  }

  /**
   * Acknowledge configuration update.
   * Sends POST /api/config/player/{playerId}/ack
   */
  async ackConfig(playerId: string): Promise<void> {
    if (!playerId) {
      console.warn('[SyncService] Cannot ack config: playerId is missing');
      return;
    }

    try {
      // Note: The controller endpoint is /api/config/player/{playerId}/ack
      console.log('[SyncService] 📤 Sending config ack:', { playerId });
      const response = await this.api.post(
        `/api/config/player/${playerId}/ack`,
      );
      console.log('[SyncService] ✅ Config ack sent successfully', {
        status: response.status,
        playerId,
      });
    } catch (error) {
      console.error('[SyncService] Failed to ack config:', error);
      // Don't throw, just log. Ack failure shouldn't crash app.
    }
  }

  /**
   * Fetches the player configuration from the server.
   * Returns mapped camelCase object or null on failure.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- config shape varia por feature flags do server; tipar exigiria types compartilhados com Java
  async fetchConfig(): Promise<any> {
    if (!this.config.deviceId) {
      console.warn('[SyncService] Cannot fetch config: No device ID');
      return null;
    }

    try {
      console.log('[SyncService] 📥 Fetching player config...');
      const response = await this.api.get('/api/config/player/me');
      const data = response.data;

      console.log('[SyncService] ✅ Config fetched successfully:', data);

      // Map snake_case to camelCase
      return {
        volumeLevel: data.volume_level ?? 50,
        resetVolumeTimeoutSeconds: data.reset_volume_timeout_seconds ?? 0,

        batteryWarningEnabled: data.battery_warning_enabled ?? true,

        audioAlertEnabled: data.audio_alert_enabled ?? true,
        audioAlertVolumeLevel: data.audio_alert_volume_level ?? 80,
        audioAlertBeepCount: data.audio_alert_beep_count ?? 3,
        audioAlertIntervalMinutes: data.audio_alert_interval_minutes ?? 5,
        audioAlertLowBatteryBeepCount:
          data.audio_alert_low_battery_beep_count ?? 5,
        audioAlertLowBatteryIntervalMinutes:
          data.audio_alert_low_battery_interval_minutes ?? 3,

        visualAlertEnabled: data.visual_alert_enabled ?? true,
        visualAlertBlinkCount: data.visual_alert_blink_count ?? 3,
        visualAlertDurationSeconds: data.visual_alert_duration_seconds ?? 30,
        visualAlertLowBatteryBlinkCount:
          data.visual_alert_low_battery_blink_count ?? 5,
        visualAlertLowBatteryDurationSeconds:
          data.visual_alert_low_battery_duration_seconds ?? 60,

        updatedAt: data.updated_at,
        // CRÍTICO: NÃO defaultar pra false. Server pode legitimamente omitir esse
        // campo (config ausente, erro de DB) — nesse caso preservar último estado
        // local. Cliente que defaulta pra false acidentalmente desativa kiosk em
        // loja sem operador quando server tem problema. Vide kiosk.store.ts:
        // setServerEnabled só é chamado se valor for explicitamente true/false.
        kioskModeEnabled: data.kiosk_mode_enabled,
        discoveryMode: data.discovery_mode,
        kioskUnlockPinHashes:
          data.kiosk_unlock_pin_hashes ?? data.kioskUnlockPinHashes ?? [],
        backgroundPortraitUrl: data.background_portrait_url ?? undefined,
        backgroundLandscapeUrl: data.background_landscape_url ?? undefined,
        playerId: data.player_id ?? data.playerId,
        // V046 — brilho. Mesmo issue do SSE: backend manda snake_case e
        // ConfigManager espera camelCase. Sem mapping aqui, fetchConfig do
        // boot devolvia objeto sem brightnessAuto → applyBrightness caía
        // em setAuto() ignorando a config do admin.
        brightnessAuto: data.brightness_auto ?? data.brightnessAuto,
        brightnessLevel: data.brightness_level ?? data.brightnessLevel,
        // Cadência do relatório de playback, decidida pelo SERVIDOR.
        //   ausente → legado (15s + a cada troca de slot). É o que servidor antigo
        //             devolve, e também o que a flag de kill-switch produz.
        //   0       → não reporta sozinho; só responde ao comando SSE.
        //   N > 0   → a cada N segundos, sem o disparo por troca de slot.
        // Deliberadamente SEM `?? default`: aqui o `undefined` é informação ("o
        // servidor não mandou"), e colapsá-lo num número apagaria o back-compat.
        playbackReportIntervalSeconds:
          data.playback_report_interval_seconds ??
          data.playbackReportIntervalSeconds,
      };
    } catch (error) {
      console.warn(
        '[SyncService] Failed to fetch config (using defaults):',
        error,
      );
      return null;
    }
  }

  /**
   * Update the device ID used for sync and SSE.
   * This is called when the authentication flow resolves the real hardware ID.
   */
  /**
   * Define as tags do HOST embedded (Fase 4a/§16). Chamado pelo canal embedded
   * a partir do `start(config).tags` do host. Normaliza (trim/dedupe/cap),
   * persiste em localStorage, e — se o set MUDOU — dispara um sync imediato
   * best-effort pra propagar rápido (o admin já vê a tag sem esperar o poll).
   *
   * NÃO é chamado por nenhum player não-embedded → `hostTags` fica `null` → o
   * campo `tags` nunca viaja (frota atual intacta).
   */
  setHostTags(tags: string[] | null | undefined): void {
    const normalized = SyncService.normalizeHostTags(tags);
    const changed =
      this.hostTags === null ||
      JSON.stringify(normalized) !== JSON.stringify(this.hostTags);
    this.hostTags = normalized;
    SyncService.persistHostTags(normalized);
    if (!changed) return;

    console.log('[SyncService] Host tags atualizadas:', normalized);
    // Propaga já (best-effort — não bloqueia; erro cai no fluxo normal de retry).
    void this.sync().catch((err) =>
      console.warn(
        '[SyncService] sync pós-setHostTags falhou (best-effort):',
        err,
      ),
    );
  }

  /** trim + drop-blank + dedupe case-insensitive + cap quantidade/tamanho. */
  private static normalizeHostTags(
    tags: string[] | null | undefined,
  ): string[] {
    if (!Array.isArray(tags)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of tags) {
      if (typeof raw !== 'string') continue;
      let t = raw.trim();
      if (!t) continue;
      if (t.length > SyncService.HOST_TAG_MAX_LEN) {
        t = t.slice(0, SyncService.HOST_TAG_MAX_LEN);
      }
      const lower = t.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      out.push(t);
      if (out.length >= SyncService.HOST_TAGS_MAX) break;
    }
    return out;
  }

  private static loadPersistedHostTags(): string[] | null {
    try {
      const raw = localStorage.getItem(SyncService.HOST_TAGS_STORAGE_KEY);
      if (raw === null) return null;
      const parsed = JSON.parse(raw);
      return SyncService.normalizeHostTags(parsed);
    } catch {
      return null;
    }
  }

  private static persistHostTags(tags: string[]): void {
    try {
      localStorage.setItem(
        SyncService.HOST_TAGS_STORAGE_KEY,
        JSON.stringify(tags),
      );
    } catch {
      // Best-effort — localStorage indisponível não pode quebrar o sync.
    }
  }

  updateDeviceId(deviceId: string): void {
    // Skip if unchanged
    if (this.config.deviceId === deviceId) return;

    // Block temporary IDs from updates too
    if (this.isTemporaryId(deviceId)) {
      console.log(
        `[SyncService] Skipping Device ID update - temporary ID (${deviceId}). Waiting for valid auth.`,
      );
      return;
    }

    console.log('[SyncService] Updating Device ID:', deviceId);
    this.config.deviceId = deviceId;

    // Update Axios headers for future requests
    this.api.defaults.headers.common['X-Device-Id'] = deviceId;

    // Update SSE service if initialized
    if (this.sseService) {
      this.sseService.updateDeviceId(deviceId);
    } else if (this.unifiedContentUnsub) {
      // Epic D: conteúdo pelo canal unificado — a identidade da conexão é do
      // RemoteOps (configurado à parte). Só o this.config.deviceId importa aqui
      // (já atualizado acima) pro filtro de eventos targeted. Nada a reconectar.
      console.debug(
        '[SyncService] Device ID atualizado — conteúdo pelo canal unificado, nada a reconectar',
      );
    } else if (
      this.config.sseEnabled &&
      this.config.sseApiUrl &&
      !this.isTemporaryId(deviceId)
    ) {
      // Logic to lazy-init SSE if it was skipped due to temp ID
      console.log('[SyncService] Valid ID received, initializing SSE now');
      this.initSSE();

      // Captura a referência e re-amplia o tipo: o TS estreitou this.sseService
      // para null no 'if' acima, mas initSSE() pode tê-la definido (modo polling).
      const sse = this.sseService as IContentDiscoveryService | null;
      if (sse) {
        sse.connect();
      }
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Troca o mecanismo de descoberta quando o servidor muda de ideia.
   *
   * O servidor decide pelo tamanho da frota do tenant e informa em toda
   * resposta de sync. Um tenant que cruza 5.000 telas migra a frota inteira
   * <b>sem republicar o player</b> — o que importa quando são milhares de TVs
   * em loja e cada release exige rollout de OTA.
   *
   * A troca derruba a conexão atual e sobe a nova. É cara, e por isso o
   * servidor aplica histerese (liga a consulta em 5.000, só volta ao stream
   * abaixo de 4.000): sem essa banda, um tenant oscilando em torno do limiar
   * faria a frota reconectar a cada sync.
   */
  private applyDiscoveryMode(mode?: 'sse' | 'polling'): void {
    // Ausente = servidor antigo ou campo não enviado. Preserva o que está em
    // uso em vez de assumir um default — trocar o mecanismo da frota por causa
    // de um campo ausente seria o pior tipo de surpresa.
    if (!mode || mode === this.discoveryMode) {
      return;
    }

    console.log(
      `[SyncService] 🔀 Servidor mudou o mecanismo de descoberta: ${this.discoveryMode} → ${mode}`,
    );
    this.discoveryMode = mode;
    this.recreateContentService();
  }

  /**
   * Derruba o serviço de descoberta atual e sobe outro no mecanismo vigente,
   * religando todos os callbacks.
   */
  private recreateContentService(): void {
    // Derruba QUALQUER transporte de conteúdo atual — 2ª conexão discreta OU
    // inscrição no canal unificado — antes de subir o do modo vigente.
    if (this.sseService) {
      this.sseService.disconnect();
      this.sseService = null;
    }
    this.unsubscribeUnifiedContent();

    // Epic D (Fase D2): em modo SSE o conteúdo passa pelo canal unificado
    // (RemoteOps) — sem 2ª conexão. Em modo polling, cria o serviço de consulta.
    if (this.shouldUseUnifiedContent()) {
      this.subscribeUnifiedContent();
      return;
    }

    this.sseService = createContentService({
      apiUrl: this.config.sseApiUrl,
      deviceId: this.config.deviceId,
      tenantId: this.config.tenantId,
      mode: this.discoveryMode,
    });
    this.wireContentServiceCallbacks();
    this.sseService.connect();
  }

  private notifySyncComplete(result: SyncResult): void {
    // Toda resposta de sync passa por aqui, inclusive o short-circuit por hash
    // — por isso é o lugar certo para reagir a uma troca de mecanismo.
    this.applyDiscoveryMode(result.discoveryMode);

    this.syncCallbacks.forEach((callback) => {
      try {
        callback(result);
      } catch (error) {
        console.error('[SyncService] Error in sync callback:', error);
      }
    });
  }

  private notifyConfigUpdate(config: {
    playerId?: string;
    volumeLevel?: number;
    resetVolumeTimeoutSeconds?: number;
    batteryWarningEnabled?: boolean;
    audioAlertEnabled?: boolean;
    audioAlertVolumeLevel?: number;
    audioAlertBeepCount?: number;
    audioAlertIntervalMinutes?: number;
    audioAlertLowBatteryBeepCount?: number;
    audioAlertLowBatteryIntervalMinutes?: number;
    visualAlertEnabled?: boolean;
    visualAlertBlinkCount?: number;
    visualAlertDurationSeconds?: number;
    visualAlertLowBatteryBlinkCount?: number;
    visualAlertLowBatteryDurationSeconds?: number;
    backgroundPortraitUrl?: string;
    backgroundLandscapeUrl?: string;
    kioskModeEnabled?: boolean;
    kioskUnlockPinHashes?: string[];
    brightnessAuto?: boolean;
    brightnessLevel?: number;
  }): void {
    this.configCallbacks.forEach((callback) => {
      try {
        callback(config);
      } catch (error) {
        console.error('[SyncService] Error in config callback:', error);
      }
    });
  }

  private notifyConnectivityChange(online: boolean): void {
    this.connectivityCallbacks.forEach((callback) => {
      try {
        callback(online);
      } catch (error) {
        console.error('[SyncService] Error in connectivity callback:', error);
      }
    });
  }

  private handleOnline = (): void => {
    console.log('[SyncService] Device came online');
    this.notifyConnectivityChange(true);

    // Trigger sync when coming back online
    if (this.pollingInterval) {
      this.sync();
    }

    // Re-arma o serviço de descoberta na volta da rede. Em modo polling o
    // ContentPollingService já reage à rede pelo próprio backoff, mas chamar
    // aqui é barato e idempotente (ensureConnected no-op se já conectando/
    // conectado) — rede de segurança se o serviço não religar sozinho.
    this.ensureSSEConnected();
  };

  private handleOffline = (): void => {
    console.log('[SyncService] Device went offline');
    this.notifyConnectivityChange(false);
  };

  // ===========================================================================
  // TRIGGER EVENT METHODS
  // ===========================================================================

  onTriggerFired(callback: TriggerFiredCallback): () => void {
    this.triggerFiredCallbacks.add(callback);
    return () => {
      this.triggerFiredCallbacks.delete(callback);
    };
  }

  onTriggerDeactivated(callback: TriggerDeactivatedCallback): () => void {
    this.triggerDeactivatedCallbacks.add(callback);
    return () => {
      this.triggerDeactivatedCallbacks.delete(callback);
    };
  }

  /** Subscribe ao SSE event "sync_correction" (impulso do supervisor de sincronismo). */
  onSyncCorrection(callback: SyncCorrectionCallback): () => void {
    this.syncCorrectionCallbacks.add(callback);
    return () => {
      this.syncCorrectionCallbacks.delete(callback);
    };
  }

  private notifySyncCorrection(payload: SyncCorrectionPayload): void {
    this.syncCorrectionCallbacks.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error(
          '[SyncService] Error in sync correction callback:',
          error,
        );
      }
    });
  }

  /** Subscribe ao SSE event "restart_command" (PLAYER_RESTART_PLAN). */
  onRestartCommand(callback: RestartCommandCallback): () => void {
    this.restartCommandCallbacks.add(callback);
    return () => {
      this.restartCommandCallbacks.delete(callback);
    };
  }

  private notifyRestartCommand(payload: RestartCommandPayload): void {
    this.restartCommandCallbacks.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error(
          '[SyncService] Error in restart command callback:',
          error,
        );
      }
    });
  }

  /**
   * Subscribe ao SSE event "player_orientation_changed" — disparado quando
   * o admin troca paisagem ↔ retrato no dropdown da Tabela de Players.
   * Listener tipicamente aplica override + forceResync pra trocar conteúdo.
   */
  onOrientationChangeFromAdmin(
    callback: (orientation: 'landscape' | 'portrait') => void,
  ): () => void {
    this.orientationFromAdminCallbacks.add(callback);
    return () => {
      this.orientationFromAdminCallbacks.delete(callback);
    };
  }

  private notifyOrientationChangeFromAdmin(
    orientation: 'landscape' | 'portrait',
  ): void {
    this.orientationFromAdminCallbacks.forEach((callback) => {
      try {
        callback(orientation);
      } catch (error) {
        console.error(
          '[SyncService] Error in orientation-from-admin callback:',
          error,
        );
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- payload SSE varia por trigger; tipar exigiria union de todos os triggers
  private notifyTriggerFired(payload: any): void {
    this.triggerFiredCallbacks.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error('[SyncService] Error in trigger fired callback:', error);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- payload SSE varia por trigger; tipar exigiria union de todos os triggers
  private notifyTriggerDeactivated(payload: any): void {
    this.triggerDeactivatedCallbacks.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error(
          '[SyncService] Error in trigger deactivated callback:',
          error,
        );
      }
    });
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  destroy(): void {
    this.stopPolling();
    this.syncBeatUnsub?.();
    this.syncBeatUnsub = null;
    this.syncCallbacks.clear();
    this.connectivityCallbacks.clear();
    this.triggerFiredCallbacks.clear();
    this.triggerDeactivatedCallbacks.clear();
    this.syncCorrectionCallbacks.clear();

    // Disconnect SSE (2ª conexão discreta) e/ou cancela a inscrição no canal
    // unificado (Epic D) — só um dos dois está ativo por vez.
    if (this.sseService) {
      this.sseService.disconnect();
      this.sseService = null;
    }
    this.unsubscribeUnifiedContent();

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
  }
}

export default SyncService;
